// PaymentRefundDetails.js
import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Button,
  Card,
  Flex,
  Heading,
  Text,
  Spacer,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Select,
  Input,
  Badge,
  Spinner,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Grid,
  VStack,
  HStack,
  keyframes
} from "@chakra-ui/react";
import { BiArrowBack } from "react-icons/bi";
import { baseUrl } from "../../utils/utils";
import CustomButton from "../../components/CustomButton";
import { get_bank_list } from "../../redux/bankSlice";
import { get_dp_vehicle_list, get_vehicle_driver_list } from '../../redux/orderSlice';
import { get_vehicle_list } from '../../redux/vehiclSlice';
import { get_driver_list } from '../../redux/driverSlice';
import { get_dp_list } from '../../redux/dplistSlice';
import SearchableDropdown from "../../components/SearchableDropdown";

const DisplayData = ({ title, value, fs = "14px", v_color = "#333", fw = "normal" }) => (
  <Box mb={2}>
    <Text fontSize={fs} color={v_color} fontWeight={fw}>
      {title && `${title}: `}{value || "NA"}
    </Text>
  </Box>
);

const DetailRow = ({ emoji, label, value }) => {
  return (
    <Flex align="flex-start" gap={3}>
      {emoji && (
        <Box bg="white" p={2} borderRadius="lg" boxShadow="sm" mt={0.5}>
          <Text fontSize="md">{emoji}</Text>
        </Box>
      )}
      <Box>
        <Text color="gray.500" fontSize="sm" fontWeight="medium">
          {label}
        </Text>
        <Text color="gray.900" fontWeight="semibold" mt={0.5}>
          {value}
        </Text>
      </Box>
    </Flex>
  );
};

const InfoCard = ({ emoji, title, value, gradient }) => {
  return (
    <Box
      bg="white"
      borderRadius="xl"
      p={6}
      border="1px solid"
      borderColor="gray.200"
      _hover={{
        boxShadow: "lg",
        transform: "translateY(-4px)"
      }}
      transition="all 0.3s ease"
    >
      <Flex align="center" gap={4}>
        <Box bgGradient={gradient} p={3} borderRadius="xl">
          <Text fontSize="2xl">{emoji}</Text>
        </Box>
        <Box>
          <Text color="gray.500" fontSize="sm" fontWeight="medium">
            {title}
          </Text>
          <Text color="gray.900" fontSize="xl" fontWeight="bold" mt={1}>
            {value}
          </Text>
        </Box>
      </Flex>
    </Box>
  );
};

const PaymentRefundDetails = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const dispatch = useDispatch();

  const { bank_list, loading: bankLoading } = useSelector((state) => state.banks);
  const {
    dp_vehicle_list,
    vehicle_driver_list,
    vehicle_loading,
    driver_loading
  } = useSelector((state) => state.orders);
  const { dp_list } = useSelector((state) => state.dplist);
  const { driver_list } = useSelector((state) => state.drivers);
  const { vehicle_list } = useSelector((state) => state.vehicles);

  const [loading, setLoading] = useState({});
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedProductIndex, setSelectedProductIndex] = useState(null);
  const [refundData, setRefundData] = useState(state?.refund || null);

  const prevPage = state?.prevPage || 1;
  const prevRows = state?.prevRows || 5;
  const prevInitialValue = state?.prevInitialValue || 1;


  const paymentValidationSchema = Yup.object().shape({
    payment_mode: Yup.string().required('Payment mode is required'),
    cash_received: Yup.number().when('payment_mode', {
      is: '1',
      then: (schema) => schema
        .required('Cash amount is required')
        .positive('Amount must be greater than 0')
        .min(0.01, 'Amount must be at least 0.01'),
      otherwise: (schema) => schema.notRequired()
    }),
    online_received: Yup.number().when('payment_mode', {
      is: '2',
      then: (schema) => schema
        .required('Online amount is required')
        .positive('Amount must be greater than 0')
        .min(0.01, 'Amount must be at least 0.01'),
      otherwise: (schema) => schema.notRequired()
    }),
    bank_id: Yup.string().when('payment_mode', {
      is: '2',
      then: (schema) => schema.required('Bank is required'),
      otherwise: (schema) => schema.notRequired()
    }),
    transaction_mode: Yup.string().when('payment_mode', {
      is: '2',
      then: (schema) => schema.required('Transaction mode is required'),
      otherwise: (schema) => schema.notRequired()
    }),
    transaction_number: Yup.string().when('payment_mode', {
      is: '2',
      then: (schema) => schema.required('Transaction number is required').trim(),
      otherwise: (schema) => schema.notRequired()
    }),
    amount: Yup.string().required()
  });

  const paymentFormik = useFormik({
    initialValues: {
      bank_id: "",
      payment_mode: "",
      amount: "",
      transaction_mode: "",
      cash_received: "",
      online_received: "",
      transaction_number: ""
    },
    validationSchema: paymentValidationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values) => {
      handleStatusUpdate(3, selectedProductIndex);
    }
  });

  const [assignData, setAssignData] = useState({
    dplist: "",
    vehicle: "",
    driver: ""
  });

  const transactionModes = [
    { label: 'Phonepay', value: '1' },
    { label: 'GPay', value: '2' },
    { label: 'Paytm', value: '3' },
    { label: 'Cred', value: '4' },
    { label: 'RTGS', value: '6' },
    { label: 'NEFT', value: '7' },
    { label: 'IMPS', value: '8' },
    { label: 'Others', value: '5' },
  ];

  const showPickupDetails = refundData?.details?.some(detail => detail.status === "2");

  const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  `;

  useEffect(() => {
    dispatch(get_bank_list({ page: 1, rows: 100 }));
    dispatch(get_vehicle_list());
    dispatch(get_driver_list());
    dispatch(get_dp_list({ page: 1, rows: 100 }));
  }, [dispatch]);

  useEffect(() => {
    if (assignData.dplist) {
      dispatch(get_dp_vehicle_list(assignData.dplist));
      setAssignData(prev => ({
        ...prev,
        vehicle: "",
        driver: ""
      }));
    }
  }, [assignData.dplist, dispatch]);

  useEffect(() => {
    if (assignData.vehicle) {
      dispatch(get_vehicle_driver_list(assignData.vehicle));
      setAssignData(prev => ({
        ...prev,
        driver: ""
      }));
    }
  }, [assignData.vehicle, dispatch]);

  const refreshRefundData = useCallback(async () => {
    try {
      const formData = new FormData();
      formData.append("action", "list");
      formData.append("page", "1");
      formData.append("rows", "1000");

      const response = await fetch(baseUrl + "payment_refund.php", {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (data.success && data.data) {
        const updatedRefund = data.data.find(item => item.id === refundData.id);
        if (updatedRefund) {
          setRefundData(updatedRefund);
        }
      }
    } catch (error) {
      console.error("Error refreshing refund data:", error);
    }
  }, [refundData?.id]);

  const getBankName = useCallback((bankId) => {
    if (!bankId || bankId === "0") return "NA";
    const bank = bank_list.find(b => b.id === bankId || b.id === parseInt(bankId));
    return bank ? bank.bankname : "NA";
  }, [bank_list]);

  const getPaymentModeLabel = (modeValue) => {
    switch (String(modeValue)) {
      case "1": return "Cash";
      case "2": return "Online";
      case "3": return "Online";
      default: return "NA";
    }
  };

  const getTransactionModeLabel = (modeValue) => {
    const mode = transactionModes.find(m => m.value === String(modeValue));
    return mode ? mode.label : "NA";
  };

  const getStatusConfig = (status) => {
    const configs = {
      "0": { label: "Pending Assignment", colorScheme: "yellow", icon: "⏳" },
      "1": { label: "Assigned", colorScheme: "blue", icon: "📋" },
      "2": { label: "DP Approved", colorScheme: "purple", icon: "⏰" },
      "3": { label: "Payment Complete", colorScheme: "green", icon: "✓" },
      "4": { label: "Rejected", colorScheme: "red", icon: "✗" }
    };
    return configs[status] || configs["0"];
  };

  const getStatusBadge = (status) => {
    const statusConfig = getStatusConfig(status);
    return (
      <Badge
        colorScheme={statusConfig.colorScheme}
        fontSize="md"
        px={5}
        py={2.5}
        borderRadius="full"
        boxShadow="lg"
        display="flex"
        alignItems="center"
        gap={2}
      >
        <Text fontSize="lg">{statusConfig.icon}</Text>
        <Text fontWeight="semibold">{statusConfig.label}</Text>
      </Badge>
    );
  };

  const handleApproveClick = useCallback((productIndex, securityDeposit) => {
    setSelectedProductIndex(productIndex);
    paymentFormik.resetForm({
      values: {
        amount: securityDeposit,
        cash_received: securityDeposit,
        online_received: securityDeposit,
        bank_id: "",
        payment_mode: "",
        transaction_mode: "",
        transaction_number: ""
      }
    });
    setIsApproveModalOpen(true);
  }, [paymentFormik]);

  const handleAssignClick = useCallback((productIndex) => {
    setSelectedProductIndex(productIndex);
    setAssignData({
      dplist: "",
      vehicle: "",
      driver: ""
    });
    setIsAssignModalOpen(true);
  }, []);

  const handlePaymentModeChange = useCallback((e) => {
    const { value } = e.target;
    paymentFormik.setFieldValue('payment_mode', value);

    if (value === "1") {
      paymentFormik.setFieldValue('online_received', '');
      paymentFormik.setFieldValue('bank_id', '');
      paymentFormik.setFieldValue('transaction_mode', '');
      paymentFormik.setFieldValue('transaction_number', '');
    } else {
      paymentFormik.setFieldValue('cash_received', '');
    }
  }, [paymentFormik]);

  // const handlePaymentDetailChange = useCallback((e) => {
  //   const { name, value } = e.target;
  //   setPaymentDetails(prev => ({ ...prev, [name]: value }));
  // }, []);

  const handleAssignDataChange = useCallback((e) => {
    const { name, value } = e.target;
    setAssignData(prev => ({ ...prev, [name]: value }));
  }, []);

  // const validatePaymentDetails = () => {
  //   if (!paymentDetails.payment_mode) {
  //     toast({
  //       title: "Error",
  //       description: "Please select payment mode",
  //       status: "error",
  //       duration: 3000,
  //       isClosable: true,
  //     });
  //     return false;
  //   }

  //   if (paymentDetails.payment_mode === "0") {
  //     if (!paymentDetails.cash_received || parseFloat(paymentDetails.cash_received) <= 0) {
  //       toast({
  //         title: "Error",
  //         description: "Please enter valid cash amount",
  //         status: "error",
  //         duration: 3000,
  //         isClosable: true,
  //       });
  //       return false;
  //     }
  //   } else {
  //     if (!paymentDetails.online_received || parseFloat(paymentDetails.online_received) <= 0) {
  //       toast({
  //         title: "Error",
  //         description: "Please enter valid online amount",
  //         status: "error",
  //         duration: 3000,
  //         isClosable: true,
  //       });
  //       return false;
  //     }
  //     if (!paymentDetails.bank_id) {
  //       toast({
  //         title: "Error",
  //         description: "Please select a bank",
  //         status: "error",
  //         duration: 3000,
  //         isClosable: true,
  //       });
  //       return false;
  //     }
  //     if (!paymentDetails.transaction_number) {
  //       toast({
  //         title: "Error",
  //         description: "Please enter transaction number",
  //         status: "error",
  //         duration: 3000,
  //         isClosable: true,
  //       });
  //       return false;
  //     }
  //   }

  //   return true;
  // };

  const validateAssignDetails = () => {
    if (!assignData.dplist) {
      toast({
        title: "Error",
        description: "Please select Delivery Partner",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }
    if (!assignData.vehicle) {
      toast({
        title: "Error",
        description: "Please select Vehicle",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }
    if (!assignData.driver) {
      toast({
        title: "Error",
        description: "Please select Driver",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }
    return true;
  };

  const handleStatusUpdate = useCallback(async (status, productIndex = null) => {
    // if (status === 1 && !validatePaymentDetails()) return;

    const detail = productIndex !== null ? refundData.details[productIndex] : null;
    const loadingKey = productIndex !== null ? `${status}_${productIndex}` : status;

    setLoading(prev => ({ ...prev, [loadingKey]: true }));

    try {
      const formData = new FormData();
      formData.append("action", "update");
      formData.append("details_id[]", detail.id);
      formData.append("status[]", status.toString());
      formData.append("userid", refundData.userid);
      formData.append("orderid", refundData.orderid);
      formData.append("prod_code[]", detail.prod_code);
      formData.append("return_cylinder[]", detail.return_cylinder);
      formData.append("security_deposit[]", detail.security_deposit);
      formData.append(
        "payment_mode[]",
        status === 1 || status === 3
          ? (paymentFormik.values.payment_mode === "2" ? "3" : paymentFormik.values.payment_mode)
          : ""
      );
      formData.append(
        "bank_id[]",
        status === 1 || status === 3 ? (paymentFormik.values.bank_id || "0") : "0"
      );
      formData.append(
        "total_amount[]",
        status === 1 || status === 3 ? (paymentFormik.values.amount || detail.security_deposit) : "0"
      );
      formData.append(
        "transaction_number[]",
        status === 1 || status === 3 ? (paymentFormik.values.transaction_number || "") : ""
      );
      formData.append(
        "cash_received[]",
        status === 1 || status === 3
          ? paymentFormik.values.payment_mode === "1"
            ? paymentFormik.values.cash_received
            : "0.00"
          : "0.00"
      );
      formData.append(
        "online_received[]",
        status === 1 || status === 3
          ? paymentFormik.values.payment_mode === "2"
            ? paymentFormik.values.online_received
            : "0.00"
          : "0.00"
      );
      formData.append(
        "mode_of_transaction[]",
        status === 1 || status === 3
          ? paymentFormik.values.payment_mode === "1"
            ? "0"
            : paymentFormik.values.transaction_mode
          : "0"
      );
      formData.append("id", refundData.id);

      const response = await fetch(baseUrl + "payment_refund.php", {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        toast({
          title: data.message || `Product ${status === 1 ? 'approved' : 'rejected'} successfully`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });


        const updatedDetails = [...refundData.details];
        updatedDetails[productIndex] = {
          ...updatedDetails[productIndex],
          status: status.toString(),
          ...(status === 1 && {
            payment_mode: paymentFormik.values.payment_mode === "2" ? "3" : paymentFormik.values.payment_mode,
            bank_id: paymentFormik.values.bank_id,
            transaction_number: paymentFormik.values.transaction_number,
            cash_received: paymentFormik.values.payment_mode === "1" ? paymentFormik.values.cash_received : "0.00",
            online_received: paymentFormik.values.payment_mode === "2" ? paymentFormik.values.online_received : "0.00",
            mode_of_transaction: paymentFormik.values.payment_mode === "1" ? "0" : paymentFormik.values.transaction_mode,
            updated_datetime: new Date().toISOString().slice(0, 19).replace('T', ' ')
          })
        };

        setRefundData(prev => ({
          ...prev,
          details: updatedDetails
        }));

        setTimeout(() => {
          refreshRefundData();
        }, 500);

      } else {
        throw new Error(data.message || "Failed to update status");
      }
    } catch (error) {
      console.error("Error submitting request:", error);
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(prev => ({ ...prev, [loadingKey]: false }));
      setIsApproveModalOpen(false);
      setSelectedProductIndex(null);
    }
  }, [refundData, paymentFormik.values, paymentFormik, toast, refreshRefundData]);

  const handleAssignOrder = useCallback(async () => {
    if (!validateAssignDetails()) return;

    const detail = selectedProductIndex !== null ? refundData.details[selectedProductIndex] : null;
    if (!detail) return;

    setLoading(prev => ({ ...prev, assign: true }));

    try {
      const formData = new FormData();
      formData.append("action", "assign");
      formData.append("details_id[]", detail.id);
      formData.append("status[]", "1");
      formData.append("userid", refundData.userid);
      formData.append("orderid", refundData.orderid);
      formData.append("prod_code[]", detail.prod_code);
      formData.append("id", refundData.id);
      formData.append("driver_id[]", assignData.driver);
      formData.append("vehicle_id[]", assignData.vehicle);
      formData.append("dpid[]", assignData.dplist);

      const response = await fetch(baseUrl + "payment_refund.php", {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        toast({
          title: "Order assigned successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        const updatedDetails = [...refundData.details];
        updatedDetails[selectedProductIndex] = {
          ...updatedDetails[selectedProductIndex],
          status: "1",
          assigned_at: new Date().toISOString().slice(0, 19).replace('T', ' '),
          assign_details: [{
            dpid: assignData.dplist,
            driverid: assignData.driver,
            vehicleid: assignData.vehicle,
            datetime: new Date().toISOString().slice(0, 19).replace('T', ' ')
          }],
          // ✅ Add track_data to show delivery tracking immediately
          track_data: [{
            dpid: assignData.dplist,
            driverid: assignData.driver,
            vehicleid: assignData.vehicle,
            assigned_at: new Date().toISOString().slice(0, 19).replace('T', ' '),
            datetime: new Date().toISOString().slice(0, 19).replace('T', ' '),
            // Add names from the state for immediate display
            delivery_partner_name: dp_list.find(dp => dp.id === assignData.dplist)?.name || '',
            driver_name: vehicle_driver_list.find(d => d.id === assignData.driver)?.name || '',
            vehicleno: dp_vehicle_list.find(v => v.vehicle_id === assignData.vehicle)?.vehicleno || '',
            statustext: "Assigned"
          }]
        };

        setRefundData(prev => ({
          ...prev,
          details: updatedDetails
        }));

        setIsAssignModalOpen(false);
        setSelectedProductIndex(null);
        setAssignData({
          dplist: "",
          vehicle: "",
          driver: ""
        });

        setTimeout(() => {
          refreshRefundData();
        }, 500);

      } else {
        throw new Error(data.message || "Failed to assign order");
      }
    } catch (error) {
      console.error("Error assigning order:", error);
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(prev => ({ ...prev, assign: false }));
    }
  }, [refundData, assignData, selectedProductIndex, toast, refreshRefundData]);

  const getProductType = (productName) => {
    if (!productName) return "N/A";

    const types = {
      "HPCL": "HPCL",
      "IOCL": "IOCL",
      "BPCL": "BPCL",
      "SUPERGAS": "SUPERGAS",
      "TOTALGAS": "TOTALGAS",
      "GOGAS": "GOGAS"
    };

    for (let key in types) {
      if (productName.toUpperCase().includes(key)) {
        return types[key];
      }
    }

    return "N/A";
  };

  if (!refundData) {
    return (
      <Card bg="#fff" paddingInline={5} paddingBlock={4}>
        <Text>No refund data found</Text>
        <Button mt={4} onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </Card>
    );
  }

  return (
    <>
      <Card
        bg="#fff"
        paddingInline={5}
        paddingBlock={2}
        display="flex"
        flexDirection={["column", "column", "row"]}
        alignItems={["stretch", "stretch", "center"]}
        gap={3}
        mb={4}
      >
        <Heading fontSize="xl">Payment Refund Details</Heading>
        <Spacer display={["none", "none", "block"]} />
        <CustomButton
          icon={<BiArrowBack color="#fff" />}
          title={"Back"}
          height={"30px"}
          b_color={"#000"}
          onClick={() =>
            navigate("/payment-refund", {
              state: {
                prevPage,
                prevRows,
                prevInitialValue,
              },
            })
          }
        />
      </Card>

      <Box minH="100vh" bg="linear-gradient(135deg, #f8fafc 0%, #e0e7ff 100%)" p={6} borderRadius="2xl">
        <Box maxW="1400px" mx="auto">
          {refundData.details?.map((detail, i) => {
            const statusConfig = getStatusConfig(detail.status);

            return (
              <Box
                key={detail.id}
                bg="white"
                borderRadius="3xl"
                overflow="hidden"
                boxShadow="2xl"
                border="1px solid"
                borderColor="gray.100"
                _hover={{ boxShadow: "2xl", transform: "translateY(-2px)" }}
                transition="all 0.3s ease"
                mb={6}
              >
                {/* Header Section with Gradient */}
                <Box
                  position="relative"
                  bgGradient="linear(to-r, #4F46E5, #2563EB, #0EA5E9)"
                  p={8}
                  color="white"
                  overflow="hidden"
                  boxShadow="md"
                >
                  <Box
                    position="absolute"
                    right="-40px"
                    top="-40px"
                    w="160px"
                    h="160px"
                    bg="whiteAlpha.200"
                    borderRadius="full"
                    filter="blur(80px)"
                  />
                  <Box
                    position="absolute"
                    left="-40px"
                    bottom="-40px"
                    w="160px"
                    h="160px"
                    bg="whiteAlpha.200"
                    borderRadius="full"
                    filter="blur(80px)"
                  />

                  <Flex
                    position="relative"
                    justify="space-between"
                    align="flex-start"
                    flexWrap="wrap"
                    gap={4}
                  >
                    <Box flex="1" minW="250px">
                      <HStack spacing={3} mb={2}>
                        <Text fontSize="2xl">📦</Text>
                        <Text fontSize="2xl" fontWeight="bold">
                          {detail.product_name}
                        </Text>
                      </HStack>
                      <HStack spacing={2} color="indigo.100">
                        <Text fontSize="sm">#</Text>
                        <Text fontSize="sm" fontFamily="mono">
                          {detail.prod_code}
                        </Text>
                      </HStack>
                    </Box>

                    {getStatusBadge(detail.status)}
                  </Flex>
                </Box>

                {/* Tabs */}
                <Tabs colorScheme="indigo" variant="enclosed">
                  <TabList
                    bg="linear-gradient(to-r, gray.50, white)"
                    borderBottom="2px solid"
                    borderColor="gray.200"
                    overflowX="auto"
                    css={{
                      '&::-webkit-scrollbar': { display: 'none' },
                      scrollbarWidth: 'none'
                    }}
                  >
                    <Tab
                      fontWeight="semibold"
                      _selected={{
                        color: 'indigo.600',
                        borderColor: 'indigo.600',
                        borderBottomWidth: '4px',
                        bg: 'indigo.50'
                      }}
                      py={4}
                      px={6}
                    >
                      <HStack spacing={2}>
                        <Text>📄</Text>
                        <Text>Product Details</Text>
                      </HStack>
                    </Tab>

                    {detail.status === "3" && (
                      <Tab
                        fontWeight="semibold"
                        _selected={{
                          color: 'green.600',
                          borderColor: 'green.600',
                          borderBottomWidth: '4px',
                          bg: 'green.50'
                        }}
                        py={4}
                        px={6}
                      >
                        <HStack spacing={2}>
                          <Text>💳</Text>
                          <Text>Payment Info</Text>
                        </HStack>
                      </Tab>
                    )}

                    {detail.track_data && detail.track_data.length > 0 && (
                      <Tab
                        fontWeight="semibold"
                        _selected={{
                          color: 'blue.600',
                          borderColor: 'blue.600',
                          borderBottomWidth: '4px',
                          bg: 'blue.50'
                        }}
                        py={4}
                        px={6}
                      >
                        <HStack spacing={2}>
                          <Text>🚚</Text>
                          <Text>Delivery Tracking</Text>
                        </HStack>
                      </Tab>
                    )}

                    {detail.status === "2" && (
                      <Tab
                        fontWeight="semibold"
                        _selected={{
                          color: 'purple.600',
                          borderColor: 'purple.600',
                          borderBottomWidth: '4px',
                          bg: 'purple.50'
                        }}
                        py={4}
                        px={6}
                      >
                        <HStack spacing={2}>
                          <Text>📍</Text>
                          <Text>Pickup Details</Text>
                        </HStack>
                      </Tab>
                    )}
                  </TabList>

                  <TabPanels>
                    {/* Product Details Tab */}
                    <TabPanel p={8}>
                      <Grid
                        templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }}
                        gap={6}
                        animation={`${fadeIn} 0.4s ease-out`}
                      >
                        <InfoCard
                          emoji="📦"
                          title="Return Cylinder"
                          value={detail.return_cylinder}
                          gradient="linear(to-br, blue.500, cyan.500)"
                        />
                        <InfoCard
                          emoji="#️⃣"
                          title="Total Quantity"
                          value={detail.count}
                          gradient="linear(to-br, indigo.500, purple.500)"
                        />
                        <InfoCard
                          emoji="💰"
                          title="Security Deposit"
                          value={`₹${detail.security_deposit}`}
                          gradient="linear(to-br, green.500, emerald.500)"
                        />
                      </Grid>
                    </TabPanel>

                    {/* Payment Tab */}
                    {detail.status === "3" && (
                      <TabPanel p={8}>
                        <Box
                          bgGradient="linear(to-br, green.50, emerald.50)"
                          borderRadius="2xl"
                          p={6}
                          border="1px solid"
                          borderColor="green.200"
                          animation={`${fadeIn} 0.4s ease-out`}
                        >
                          <Text fontSize="lg" fontWeight="bold" color="green.800" mb={4}>
                            💳 Payment Information
                          </Text>
                          <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={6}>
                            <DetailRow label="Payment Mode" value={getPaymentModeLabel(detail.payment_mode)} />
                            {detail.payment_mode === "1" ? (
                              <DetailRow label="Cash Received" value={`₹${detail.cash_received || "0.00"}`} />
                            ) : (
                              <>
                                <DetailRow label="Online Received" value={`₹${detail.online_received || "0.00"}`} />
                                <DetailRow label="Bank" value={getBankName(detail.bank_id)} />
                                <DetailRow label="Transaction Mode" value={getTransactionModeLabel(detail.mode_of_transaction)} />
                                <DetailRow label="Transaction Number" value={detail.transaction_number || "NA"} />
                              </>
                            )}
                            {detail.updated_datetime && (
                              <DetailRow label="Updated At" value={detail.updated_datetime} />
                            )}
                          </Grid>
                        </Box>
                      </TabPanel>
                    )}

                    {/* Tracking Tab */}
                    {detail.track_data && detail.track_data.length > 0 && (
                      <TabPanel p={8}>
                        <VStack spacing={4} align="stretch" animation={`${fadeIn} 0.4s ease-out`}>
                          {detail.track_data.map((track, idx) => (
                            <Box
                              key={idx}
                              bgGradient="linear(to-br, blue.50, cyan.50)"
                              borderRadius="2xl"
                              p={6}
                              border="1px solid"
                              borderColor="blue.200"
                            >
                              <HStack spacing={3} mb={4}>
                                <Box bg="blue.600" p={3} borderRadius="xl">
                                  <Text fontSize="2xl">🚚</Text>
                                </Box>
                                <Text fontSize="lg" fontWeight="bold" color="blue.900">
                                  Delivery Assignment #{idx + 1}
                                </Text>
                              </HStack>
                              <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={4}>
                                <DetailRow
                                  emoji="🏢"
                                  label="Delivery Partner"
                                  value={
                                    track.delivery_partner_name ||
                                    dp_list.find(dp => dp.id === track.dpid)?.name ||
                                    track.dpid
                                  }
                                />
                                <DetailRow
                                  emoji="👤"
                                  label="Driver"
                                  value={
                                    track.driver_name ||
                                    driver_list.find(d => d.id === track.driverid)?.name ||
                                    track.driverid
                                  }
                                />
                                <DetailRow
                                  emoji="🚗"
                                  label="Vehicle"
                                  value={
                                    track.vehicleno ||
                                    vehicle_list.find(v => v.id === track.vehicleid)?.vehicleno ||
                                    track.vehicleid
                                  }
                                />
                                <DetailRow
                                  emoji="📅"
                                  label="Assigned Date"
                                  value={track.assigned_at || track.datetime || "N/A"}
                                />
                                {track.statustext && (
                                  <DetailRow emoji="✅" label="Status" value={track.statustext} />
                                )}
                              </Grid>
                            </Box>
                          ))}
                        </VStack>
                      </TabPanel>
                    )}

                    {/* Pickup Tab */}
                    {detail.status === "2" && (
                      <TabPanel p={8}>
                        <Box
                          bgGradient="linear(to-br, purple.50, pink.50)"
                          borderRadius="2xl"
                          p={6}
                          border="1px solid"
                          borderColor="purple.200"
                          animation={`${fadeIn} 0.4s ease-out`}
                        >
                          <Text fontSize="lg" fontWeight="bold" color="purple.800" mb={4}>
                            📍 Pickup Information
                          </Text>
                          <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={6}>
                            <DetailRow label="Empty Received" value={detail.empty_received} />
                            <DetailRow label="Refunded at" value={detail.refunded_at || "N/A"} />
                            <DetailRow label="Product Type" value={getProductType(detail.product_name)} />
                          </Grid>
                        </Box>
                      </TabPanel>
                    )}
                  </TabPanels>
                </Tabs>

                {/* Action Buttons */}
                {(detail.status === "0" || detail.status === "2") && (
                  <Box px={8} pb={8}>
                    <Box
                      bgGradient="linear(to-r, gray.50, slate.50)"
                      borderRadius="2xl"
                      p={6}
                      border="1px solid"
                      borderColor="gray.200"
                    >
                      <Flex justify="flex-end" gap={4} flexWrap="wrap">
                        {detail.status === "0" && (
                          <Button
                            bgGradient="linear(to-r, #2563EB, #4F46E5)"
                            color="white"
                            size="lg"
                            borderRadius="full"
                            px={10}
                            fontWeight="bold"
                            boxShadow="lg"
                            _hover={{
                              bgGradient: "linear(to-r, #1D4ED8, #4338CA)",
                              transform: "scale(1.05)",
                              boxShadow: "xl"
                            }}
                            transition="all 0.25s ease"
                            rightIcon={<Text>→</Text>}
                            isLoading={loading.assign}
                            onClick={() => handleAssignClick(i)}
                          >
                            Assign Order
                          </Button>

                        )}

                        {detail.status === "2" && (
                          <>
                            <Button
                              bgGradient="linear(to-r, red.600, pink.600)"
                              color="white"
                              size="lg"
                              borderRadius="full"
                              px={8}
                              fontWeight="semibold"
                              _hover={{
                                bgGradient: "linear(to-r, red.700, pink.700)",
                                transform: "scale(1.05)",
                                boxShadow: "xl"
                              }}
                              transition="all 0.3s ease"
                              leftIcon={<Text>✗</Text>}
                              isLoading={loading[`2_${i}`]}
                              onClick={() => handleStatusUpdate(4, i)}
                            >
                              Reject
                            </Button>
                            <Button
                              bgGradient="linear(to-r, #2563EB, #4F46E5)"
                              color="white"
                              size="lg"
                              borderRadius="full"
                              px={10}
                              fontWeight="bold"
                              boxShadow="lg"
                              _hover={{
                                bgGradient: "linear(to-r, #1D4ED8, #4338CA)",
                                transform: "scale(1.05)",
                                boxShadow: "xl"
                              }}
                              transition="all 0.25s ease"
                              leftIcon={<Text>✓</Text>}
                              isLoading={loading[`1_${i}`]}
                              onClick={() => handleApproveClick(i, detail.security_deposit)}
                            >
                              Approve
                            </Button>
                          </>
                        )}
                      </Flex>
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Approval Modal */}
      <Modal
        isOpen={isApproveModalOpen}
        onClose={() => {
          setIsApproveModalOpen(false);
          paymentFormik.resetForm();
        }}
        size={["full", "lg"]}
      >
        <ModalOverlay />
        <ModalContent m={[0, 4]} maxWidth={["100%", "lg"]}>
          <ModalHeader>Payment Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedProductIndex !== null && (
              <>
                <Box mb={4} p={3} bg="blue.50" borderRadius="md">
                  <Text fontSize="md" fontWeight="bold" mb={2}>
                    Product: {refundData.details[selectedProductIndex]?.product_name}
                  </Text>
                  <Text fontSize="lg" fontWeight="bold" color="green.600">
                    Security Deposit: ₹{paymentFormik.values.amount}
                  </Text>
                </Box>

                <FormControl
                  mb={4}
                  isRequired
                  isInvalid={paymentFormik.touched.payment_mode && paymentFormik.errors.payment_mode}
                >
                  <FormLabel>Payment Mode</FormLabel>
                  <Select
                    name="payment_mode"
                    value={paymentFormik.values.payment_mode}
                    onChange={handlePaymentModeChange}
                    onBlur={paymentFormik.handleBlur}
                    placeholder="Select payment mode"
                  >
                    <option value="1">Cash</option>
                    <option value="2">Online</option>
                  </Select>
                  {paymentFormik.touched.payment_mode && paymentFormik.errors.payment_mode && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {paymentFormik.errors.payment_mode}
                    </Text>
                  )}
                </FormControl>

                {paymentFormik.values.payment_mode === "1" ? (
                  <FormControl
                    mb={4}
                    isRequired
                    isInvalid={paymentFormik.touched.cash_received && paymentFormik.errors.cash_received}
                  >
                    <FormLabel>Cash Paid</FormLabel>
                    <Input
                      name="cash_received"
                      value={paymentFormik.values.cash_received}
                      onChange={paymentFormik.handleChange}
                      onBlur={paymentFormik.handleBlur}
                      placeholder="Enter cash amount"
                      type="number"
                      min="0"
                      step="0.01"
                    />
                    {paymentFormik.touched.cash_received && paymentFormik.errors.cash_received && (
                      <Text color="red.500" fontSize="sm" mt={1}>
                        {paymentFormik.errors.cash_received}
                      </Text>
                    )}
                  </FormControl>
                ) : paymentFormik.values.payment_mode === "2" ? (
                  <>
                    <FormControl
                      mb={4}
                      isRequired
                      isInvalid={paymentFormik.touched.online_received && paymentFormik.errors.online_received}
                    >
                      <FormLabel>Online Paid</FormLabel>
                      <Input
                        name="online_received"
                        value={paymentFormik.values.online_received}
                        onChange={paymentFormik.handleChange}
                        onBlur={paymentFormik.handleBlur}
                        placeholder="Enter online amount"
                        type="number"
                        min="0"
                        step="0.01"
                      />
                      {paymentFormik.touched.online_received && paymentFormik.errors.online_received && (
                        <Text color="red.500" fontSize="sm" mt={1}>
                          {paymentFormik.errors.online_received}
                        </Text>
                      )}
                    </FormControl>

                    <FormControl
                      mb={4}
                      isRequired
                      isInvalid={paymentFormik.touched.bank_id && paymentFormik.errors.bank_id}
                    >
                      <FormLabel>Bank</FormLabel>
                      <Select
                        name="bank_id"
                        value={paymentFormik.values.bank_id}
                        onChange={paymentFormik.handleChange}
                        onBlur={paymentFormik.handleBlur}
                        placeholder={bankLoading ? "Loading banks..." : "Select bank"}
                        isLoading={bankLoading}
                        disabled={bankLoading}
                      >
                        {bank_list.map((bank) => (
                          <option key={bank.id} value={bank.id}>
                            {bank.bankname} ({bank.bank_branch}) - {bank.accountno}
                          </option>
                        ))}
                      </Select>
                      {paymentFormik.touched.bank_id && paymentFormik.errors.bank_id && (
                        <Text color="red.500" fontSize="sm" mt={1}>
                          {paymentFormik.errors.bank_id}
                        </Text>
                      )}
                    </FormControl>

                    <FormControl
                      mb={4}
                      isRequired
                      isInvalid={paymentFormik.touched.transaction_mode && paymentFormik.errors.transaction_mode}
                    >
                      <FormLabel>Transaction Mode</FormLabel>
                      <Select
                        name="transaction_mode"
                        value={paymentFormik.values.transaction_mode}
                        onChange={paymentFormik.handleChange}
                        onBlur={paymentFormik.handleBlur}
                        placeholder="Select transaction mode"
                      >
                        {transactionModes.map((mode) => (
                          <option key={mode.value} value={mode.value}>
                            {mode.label}
                          </option>
                        ))}
                      </Select>
                      {paymentFormik.touched.transaction_mode && paymentFormik.errors.transaction_mode && (
                        <Text color="red.500" fontSize="sm" mt={1}>
                          {paymentFormik.errors.transaction_mode}
                        </Text>
                      )}
                    </FormControl>

                    <FormControl
                      mb={4}
                      isRequired
                      isInvalid={paymentFormik.touched.transaction_number && paymentFormik.errors.transaction_number}
                    >
                      <FormLabel>Transaction Number</FormLabel>
                      <Input
                        name="transaction_number"
                        value={paymentFormik.values.transaction_number}
                        onChange={paymentFormik.handleChange}
                        onBlur={paymentFormik.handleBlur}
                        placeholder="Enter transaction number"
                      />
                      {paymentFormik.touched.transaction_number && paymentFormik.errors.transaction_number && (
                        <Text color="red.500" fontSize="sm" mt={1}>
                          {paymentFormik.errors.transaction_number}
                        </Text>
                      )}
                    </FormControl>
                  </>
                ) : null}
              </>
            )}
          </ModalBody>

          <ModalFooter>
            <Button
              colorScheme="green"
              isLoading={loading[`1_${selectedProductIndex}`]}
              onClick={paymentFormik.handleSubmit}
              isDisabled={!paymentFormik.isValid || !paymentFormik.dirty}
            >
              Confirm Approval
            </Button>
            <Button
              variant="ghost"
              ml={3}
              onClick={() => {
                setIsApproveModalOpen(false);
                paymentFormik.resetForm();
              }}
            >
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {/* Assign Order Modal */}
      <Modal
        isOpen={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        isCentered
        closeOnOverlayClick={false}
        blockScrollOnMount={true}
        preserveScrollBarGap={true}
        trapFocus={true}
        returnFocusOnClose={true}
        size="xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Assign Driver & Vehicle</ModalHeader>
          <ModalCloseButton
            _focus={{
              boxShadow: '0 0 0 2px rgba(66, 153, 225, 0.6)',
            }}
          />
          <ModalBody>
            <Flex flexDirection={"column"} gap={"2"} w={"full"}>
              <FormControl display={"flex"} flexDirection={"column"} gap={"2"}>

                {/* Delivery Partner Selection */}
                <FormLabel mb={0} fontWeight={"bold"}>
                  Select Delivery Partner
                </FormLabel>

                <SearchableDropdown
                  options={dp_list || []}
                  value={assignData.dplist}    // id value stored
                  onChange={(selected) =>
                    setAssignData({
                      ...assignData,
                      dplist: selected.id       // store the ID of selected partner
                    })
                  }

                  placeholder="Select Delivery Partner"
                  searchPlaceholder="Search partner by name, email, phone..."

                  // return label for dropdown
                  getOptionLabel={(option) => option.name}

                  // return unique id
                  getOptionValue={(option) => option.id}

                  // optional — if your dp_list has email/number
                  getOptionSubLabel={(option) => option?.email || option?.mobile || ""}

                  size="md"
                  variant="outline"
                  matchWidth={true}
                  maxHeight="300px"
                />

                {/* Vehicle Selection */}
                <FormLabel mb={0} fontWeight={"bold"}>
                  Select Vehicle
                </FormLabel>
                <Select
                  value={assignData.vehicle || ""}
                  onChange={(e) =>
                    setAssignData({
                      ...assignData,
                      vehicle: e.target.value
                    })
                  }
                  placeholder="Select Vehicle"
                  size={"md"}
                  w={"full"}
                  disabled={!assignData.dplist}
                  _focus={{
                    borderColor: 'blue.400',
                    boxShadow: '0 0 0 1px rgba(66, 153, 225, 0.6)',
                  }}
                >
                  {vehicle_loading ? (
                    <option disabled>Loading vehicles...</option>
                  ) : (
                    dp_vehicle_list?.map((item) => (
                      <option key={item.vehicle_id} value={item.vehicle_id}>
                        {item.vehicleno}
                      </option>
                    ))
                  )}
                </Select>
                {vehicle_loading && (
                  <Flex align="center" gap={2}>
                    <Spinner size="sm" />
                    <Text fontSize="sm">Loading vehicles...</Text>
                  </Flex>
                )}

                {/* Driver Selection */}
                <FormLabel mb={0} fontWeight={"bold"}>
                  Select Driver
                </FormLabel>
                <Select
                  value={assignData.driver || ""}
                  onChange={(e) =>
                    setAssignData({
                      ...assignData,
                      driver: e.target.value
                    })
                  }
                  placeholder="Select Driver"
                  size={"md"}
                  w={"full"}
                  disabled={!assignData.vehicle}
                  _focus={{
                    borderColor: 'blue.400',
                    boxShadow: '0 0 0 1px rgba(66, 153, 225, 0.6)',
                  }}
                >
                  {driver_loading ? (
                    <option disabled>Loading drivers...</option>
                  ) : (
                    vehicle_driver_list?.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))
                  )}
                </Select>
                {driver_loading && (
                  <Flex align="center" gap={2}>
                    <Spinner size="sm" />
                    <Text fontSize="sm">Loading drivers...</Text>
                  </Flex>
                )}

                {/* Product Information */}
                {selectedProductIndex !== null && (
                  <Flex
                    mt={3}
                    p={2}
                    gap={3}
                    flexDirection={"column"}
                    bg={"#f8f9fa"}
                    w={"full"}
                    borderRadius={10}
                  >
                    <Text fontSize={20} fontWeight={"bold"}>
                      Product Information
                    </Text>
                    <DisplayData
                      title="Product Name"
                      value={refundData.details[selectedProductIndex]?.product_name}
                      fw="bold"
                    />
                    <DisplayData
                      title="Product Code"
                      value={refundData.details[selectedProductIndex]?.prod_code}
                    />
                    <DisplayData
                      title="Security Deposit"
                      value={`₹${refundData.details[selectedProductIndex]?.security_deposit}`}
                    />
                    <DisplayData
                      title="Return Cylinder"
                      value={refundData.details[selectedProductIndex]?.return_cylinder}
                    />
                  </Flex>
                )}
              </FormControl>
            </Flex>
          </ModalBody>

          <ModalFooter display={"flex"} w={"full"} gap={2} justifyContent={"center"}>
            <Button
              colorScheme="green"
              onClick={handleAssignOrder}
              isLoading={loading.assign}
              loadingText="Assigning..."
              disabled={loading.assign}
              _focus={{
                boxShadow: '0 0 0 2px rgba(72, 187, 120, 0.6)',
              }}
            >
              {loading.assign ? "Assigning..." : "Assign Order"}
            </Button>
            <Button
              colorScheme="red"
              onClick={() => setIsAssignModalOpen(false)}
              disabled={loading.assign}
              _focus={{
                boxShadow: '0 0 0 2px rgba(245, 101, 101, 0.6)',
              }}
            >
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

    </>
  );
};

export default PaymentRefundDetails;